import { normalizeHorarioDetallado } from "./restaurantProfile";

const STORAGE_PREFIX = "mozzoapp:branch-closing-control:";
const MINUTE_MS = 60 * 1000;

export const BRANCH_CLOSING_CONTROL_UPDATED_EVENT =
  "mozzoapp:branch-closing-control-updated";
export const BRANCH_CLOSING_CONTROL_REMOTE_SYNC_SOURCE = "remote-sync";
export const BRANCH_CLOSING_EXTENSION_OPTIONS = Object.freeze([
  5,
  10,
  15,
  20,
  30,
]);
export const BRANCH_CLOSING_INITIAL_PROMPT_GRACE_MS = 5 * MINUTE_MS;

const normalizeTimestamp = (value) => {
  const parsed = new Date(String(value || "").trim());

  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString();
};

const normalizeDecision = (value) => {
  const normalized = String(value || "").trim().toLowerCase();

  return normalized === "extend" || normalized === "close" ? normalized : "";
};

export const normalizeBranchClosingControl = (value = {}) => ({
  extensionUntil: normalizeTimestamp(
    value?.extensionUntil ?? value?.extension_until
  ),
  scheduledCloseAt: normalizeTimestamp(
    value?.scheduledCloseAt ?? value?.scheduled_close_at
  ),
  handledBoundaryAt: normalizeTimestamp(
    value?.handledBoundaryAt ?? value?.handled_boundary_at
  ),
  decision: normalizeDecision(value?.decision),
  extensionMinutes: BRANCH_CLOSING_EXTENSION_OPTIONS.includes(
    Number(value?.extensionMinutes ?? value?.extension_minutes)
  )
    ? Number(value?.extensionMinutes ?? value?.extension_minutes)
    : 0,
  updatedAt: normalizeTimestamp(value?.updatedAt ?? value?.updated_at),
  updatedBy: String(value?.updatedBy ?? value?.updated_by ?? "")
    .trim()
    .slice(0, 160),
});

export const buildBranchClosingControlStorageKey = (branchId) =>
  `${STORAGE_PREFIX}${String(branchId || "default").trim() || "default"}`;

export const getBranchClosingControl = (branch) => {
  if (typeof window === "undefined") {
    return normalizeBranchClosingControl();
  }

  try {
    const rawValue = window.localStorage.getItem(
      buildBranchClosingControlStorageKey(branch?.id)
    );

    return normalizeBranchClosingControl(rawValue ? JSON.parse(rawValue) : null);
  } catch (error) {
    console.error("No pudimos leer la prorroga de cierre de la sucursal:", error);
    return normalizeBranchClosingControl();
  }
};

export const saveBranchClosingControl = (
  branch,
  value,
  { source = "local" } = {}
) => {
  const normalized = normalizeBranchClosingControl(value);

  if (typeof window === "undefined") {
    return normalized;
  }

  try {
    window.localStorage.setItem(
      buildBranchClosingControlStorageKey(branch?.id),
      JSON.stringify(normalized)
    );
    window.dispatchEvent(
      new CustomEvent(BRANCH_CLOSING_CONTROL_UPDATED_EVENT, {
        detail: {
          branchId: branch?.id || "default",
          payload: normalized,
          source,
        },
      })
    );
  } catch (error) {
    console.error("No pudimos guardar la prorroga de cierre de la sucursal:", error);
  }

  return normalized;
};

export const selectNewestBranchClosingControl = (firstValue, secondValue) => {
  const first = normalizeBranchClosingControl(firstValue);
  const second = normalizeBranchClosingControl(secondValue);
  const firstTimestamp = new Date(first.updatedAt || 0).getTime();
  const secondTimestamp = new Date(second.updatedAt || 0).getTime();

  return secondTimestamp > firstTimestamp ? second : first;
};

const parseTimeToMinutes = (value) => {
  const [hours, minutes] = String(value || "")
    .split(":")
    .map(Number);

  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  return hours * 60 + minutes;
};

const getScheduleDayIndex = (date) => {
  const jsDay = date.getDay();
  return jsDay === 0 ? 6 : jsDay - 1;
};

const buildScheduleIntervals = (horarioDetallado, nowDate) => {
  const schedule = normalizeHorarioDetallado(horarioDetallado);
  const currentDate =
    nowDate instanceof Date ? new Date(nowDate.getTime()) : new Date(nowDate);

  if (Number.isNaN(currentDate.getTime())) {
    return [];
  }

  const intervals = [];

  for (let dayOffset = -2; dayOffset <= 1; dayOffset += 1) {
    const serviceDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate() + dayOffset
    );
    const scheduleDay = schedule[getScheduleDayIndex(serviceDate)];

    if (!scheduleDay?.abierto) {
      continue;
    }

    (scheduleDay.franjas || []).forEach((franja, shiftIndex) => {
      const openingMinutes = parseTimeToMinutes(franja?.apertura);
      const closingMinutes = parseTimeToMinutes(franja?.cierre);

      if (openingMinutes === null || closingMinutes === null) {
        return;
      }

      const openingAt = new Date(serviceDate.getTime());
      openingAt.setMinutes(openingMinutes);

      const closingAt = new Date(serviceDate.getTime());
      closingAt.setMinutes(closingMinutes);

      if (closingMinutes <= openingMinutes) {
        closingAt.setDate(closingAt.getDate() + 1);
      }

      intervals.push({
        id: `${scheduleDay.id}-${serviceDate.getFullYear()}-${serviceDate.getMonth() + 1}-${serviceDate.getDate()}-${shiftIndex}`,
        dayId: scheduleDay.id,
        openingAt: openingAt.toISOString(),
        closingAt: closingAt.toISOString(),
      });
    });
  }

  return intervals.sort(
    (first, second) =>
      new Date(first.openingAt).getTime() - new Date(second.openingAt).getTime()
  );
};

export const getBranchScheduleState = (horarioDetallado, nowDate = new Date()) => {
  const currentDate =
    nowDate instanceof Date ? new Date(nowDate.getTime()) : new Date(nowDate);
  const nowTimestamp = currentDate.getTime();
  const intervals = buildScheduleIntervals(horarioDetallado, currentDate);
  const activeWindows = intervals.filter((interval) => {
    const openingTimestamp = new Date(interval.openingAt).getTime();
    const closingTimestamp = new Date(interval.closingAt).getTime();

    return nowTimestamp >= openingTimestamp && nowTimestamp < closingTimestamp;
  });
  const activeWindow = activeWindows.sort(
    (first, second) =>
      new Date(second.closingAt).getTime() - new Date(first.closingAt).getTime()
  )[0];
  const latestClosedWindow = intervals
    .filter((interval) => new Date(interval.closingAt).getTime() <= nowTimestamp)
    .sort(
      (first, second) =>
        new Date(second.closingAt).getTime() - new Date(first.closingAt).getTime()
    )[0];

  return {
    isOpen: Boolean(activeWindow),
    activeWindow: activeWindow || null,
    latestClosedWindow: latestClosedWindow || null,
  };
};

export const getEffectiveBranchOperatingState = (
  horarioDetallado,
  nowDate = new Date(),
  closingControl = {}
) => {
  const currentDate =
    nowDate instanceof Date ? new Date(nowDate.getTime()) : new Date(nowDate);
  const nowTimestamp = currentDate.getTime();
  const scheduleState = getBranchScheduleState(horarioDetallado, currentDate);
  const normalizedControl = normalizeBranchClosingControl(closingControl);
  const extensionTimestamp = new Date(normalizedControl.extensionUntil || 0).getTime();
  const isExtensionActive = extensionTimestamp > nowTimestamp;
  const scheduledClosingTimestamp = new Date(
    scheduleState.activeWindow?.closingAt || 0
  ).getTime();
  const effectiveClosingTimestamp = Math.max(
    Number.isFinite(scheduledClosingTimestamp) ? scheduledClosingTimestamp : 0,
    isExtensionActive ? extensionTimestamp : 0
  );

  return {
    ...scheduleState,
    isScheduledOpen: scheduleState.isOpen,
    isExtensionActive,
    isOpen: scheduleState.isOpen || isExtensionActive,
    effectiveClosingAt: effectiveClosingTimestamp
      ? new Date(effectiveClosingTimestamp).toISOString()
      : "",
  };
};

export const getMostRecentBranchClosingBoundary = (
  horarioDetallado,
  nowDate = new Date(),
  closingControl = {}
) => {
  const currentDate =
    nowDate instanceof Date ? new Date(nowDate.getTime()) : new Date(nowDate);
  const nowTimestamp = currentDate.getTime();
  const scheduleState = getBranchScheduleState(horarioDetallado, currentDate);
  const normalizedControl = normalizeBranchClosingControl(closingControl);
  const candidates = [];

  if (scheduleState.latestClosedWindow?.closingAt) {
    candidates.push({
      boundaryAt: scheduleState.latestClosedWindow.closingAt,
      kind: "scheduled",
    });
  }

  const extensionTimestamp = new Date(normalizedControl.extensionUntil || 0).getTime();

  if (extensionTimestamp > 0 && extensionTimestamp <= nowTimestamp) {
    candidates.push({
      boundaryAt: new Date(extensionTimestamp).toISOString(),
      kind: "extension",
    });
  }

  return (
    candidates.sort(
      (first, second) =>
        new Date(second.boundaryAt).getTime() -
        new Date(first.boundaryAt).getTime()
    )[0] || null
  );
};

export const isBranchClosingBoundaryHandled = (closingControl, boundaryAt) =>
  Boolean(
    boundaryAt &&
      normalizeBranchClosingControl(closingControl).handledBoundaryAt ===
        normalizeTimestamp(boundaryAt)
  );

export const formatBranchClosingTime = (value) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};
